import { getImageProps } from 'next/image';

/**
 * Host suffixes whose images are served through the Next image optimizer
 * (`/_next/image`) so they arrive resized and as WebP.
 *
 * Must mirror `images.remotePatterns` in `next.config.ts`: the optimizer answers
 * 400 for any host not listed there, so anything else must render unoptimized
 * (the original URL, exactly as before).
 */
export const OPTIMIZED_IMAGE_HOST_SUFFIXES = ['.appchargestore.com', '.appcharge.com'] as const;

/**
 * Measured on King's news assets against the PNG sources: at this setting AVIF
 * (Next encodes it at quality - 20, full chroma) leaves 0.03% of samples more than
 * 20/255 off, at ~30% fewer bytes than the PNG. WebP (the fallback) is limited by
 * its chroma subsampling rather than by this number.
 */
export const CMS_IMAGE_QUALITY = 90;

/**
 * Width requested for intrinsic-size layouts (`w-auto h-auto` + `max-width: 100%`).
 * The optimizer never enlarges, so files up to this width come back at their own
 * pixel size and lay out exactly as the original; wider ones come back capped here,
 * which the layout cannot tell apart (the block container is at most 1070 CSS px,
 * so either is container-capped at the same aspect ratio).
 *
 * 2048 covers that container on a 2x screen for anything up to 1024 CSS px wide;
 * the rare wider-than-that case on a dense screen is upgraded on load (see
 * needsSharperIntrinsicImage) rather than making every phone download the
 * CMS_IMAGE_MAX_WIDTH variant.
 */
export const CMS_IMAGE_FULL_SIZE_WIDTH = 2048;

/** Largest width the optimizer serves (the top of Next's default deviceSizes). */
export const CMS_IMAGE_MAX_WIDTH = 3840;

/** Post card thumbnail slot: full width on mobile, capped at 472px in the desktop news layout. */
export const POST_THUMBNAIL_SIZES = '(min-width: 1024px) 472px, 100vw';

export interface ImageSource {
  src: string;
  srcSet?: string;
  sizes?: string;
}

export const isOptimizableImageSrc = (src: string | undefined | null): boolean => {
  if (!src) return false;

  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return false;
  }

  if (url.protocol !== 'https:') return false;

  const host = url.hostname.toLowerCase();
  return OPTIMIZED_IMAGE_HOST_SUFFIXES.some(
    (suffix) => host.endsWith(suffix) && host.length > suffix.length
  );
};

/**
 * Variant of a CMS image for layouts that follow the file's intrinsic size
 * (`w-auto h-auto`): same pixels up to `width`, served as AVIF/WebP.
 */
export const getFullSizeCmsImageSrc = (
  src: string,
  width: number = CMS_IMAGE_FULL_SIZE_WIDTH
): string => {
  if (!isOptimizableImageSrc(src)) return src;

  const { props } = getImageProps({
    src,
    alt: '',
    width,
    height: width,
    quality: CMS_IMAGE_QUALITY,
  });

  // For a fixed width next/image emits "<w> 1x, <2w> 2x" and reports the 2x URL as
  // src; the 1x candidate is the exact width asked for.
  return props.srcSet?.split(',')[0]?.trim().split(' ')[0] ?? props.src;
};

interface LoadedIntrinsicImage {
  /** Pixel width of the file that arrived. */
  naturalWidth: number;
  /** Laid-out width in CSS px. */
  renderedWidth: number;
  devicePixelRatio: number;
}

/**
 * Whether a loaded intrinsic-size image should be swapped for the
 * CMS_IMAGE_MAX_WIDTH variant: the file came back capped (so a wider one
 * exists) and the screen paints more device pixels than it holds. The swap
 * cannot move the layout, as both variants are wider than the container.
 */
export const needsSharperIntrinsicImage = ({
  naturalWidth,
  renderedWidth,
  devicePixelRatio,
}: LoadedIntrinsicImage): boolean =>
  naturalWidth >= CMS_IMAGE_FULL_SIZE_WIDTH &&
  naturalWidth < CMS_IMAGE_MAX_WIDTH &&
  renderedWidth * devicePixelRatio > naturalWidth;

/**
 * The exact candidates a `<CmsImage fill sizes={sizes}>` renders, so a preload
 * (`new Image()`) warms the same cache entry the component will use.
 */
export const getCmsImageFillSource = (src: string, sizes: string): ImageSource => {
  if (!isOptimizableImageSrc(src)) return { src };

  const { props } = getImageProps({ src, alt: '', fill: true, sizes, quality: CMS_IMAGE_QUALITY });

  return { src: props.src, srcSet: props.srcSet, sizes: props.sizes };
};
